import { getThemeById, type ThemeColors } from './themes';

const LIGHT_THEME_KEY = 'colorTheme:light';
const DARK_THEME_KEY = 'colorTheme:dark';
// Legacy key for migration
const LEGACY_KEY = 'colorTheme';

function applyColors(colors: ThemeColors) {
  const root = document.documentElement;
  Object.entries(colors).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  // Map theme CSS variables to shadcn/ui CSS variables
  root.style.setProperty('--background', colors['--theme-background']);
  root.style.setProperty('--primary', colors['--theme-primary']);
  root.style.setProperty('--primary-foreground', colors['--theme-primary-foreground']);
  root.style.setProperty('--accent', colors['--theme-accent']);
  root.style.setProperty('--accent-foreground', colors['--theme-accent-foreground']);
}

function clearThemeColors() {
  const root = document.documentElement;
  const keys = [
    '--theme-background', '--theme-primary', '--theme-primary-foreground',
    '--theme-accent', '--theme-accent-foreground',
    '--theme-sidebar', '--theme-card-border',
    '--background', '--primary', '--primary-foreground',
    '--accent', '--accent-foreground',
  ];
  keys.forEach(key => root.style.removeProperty(key));
}

function isDarkMode(): boolean {
  return document.documentElement.classList.contains('dark');
}

function getKeyForMode(): string {
  return isDarkMode() ? DARK_THEME_KEY : LIGHT_THEME_KEY;
}

/** Apply theme colors visually without persisting to localStorage */
export function previewTheme(themeId: string) {
  const isDefault = themeId === 'default' || themeId === 'dark-default';
  if (isDefault) {
    clearThemeColors();
    return;
  }
  const theme = getThemeById(themeId);
  if (!theme) {
    clearThemeColors();
    return;
  }
  const colors = isDarkMode() ? theme.dark : theme.light;
  applyColors(colors);
}

export function applyTheme(themeId: string) {
  const isDefault = themeId === 'default' || themeId === 'dark-default';

  if (isDefault) {
    clearThemeColors();
    localStorage.setItem(getKeyForMode(), themeId);
    return;
  }

  const theme = getThemeById(themeId);
  if (!theme) {
    clearThemeColors();
    return;
  }

  const colors = isDarkMode() ? theme.dark : theme.light;
  applyColors(colors);
  localStorage.setItem(getKeyForMode(), themeId);
}

export function loadSavedTheme() {
  // Migrate legacy key
  const legacy = localStorage.getItem(LEGACY_KEY);
  if (legacy && legacy !== 'default') {
    localStorage.setItem(LIGHT_THEME_KEY, legacy);
    localStorage.removeItem(LEGACY_KEY);
  }

  const themeId = getSavedThemeId();
  if (themeId !== 'default' && themeId !== 'dark-default') {
    applyTheme(themeId);
  }
}

export function getSavedThemeId(): string {
  const key = getKeyForMode();
  const saved = localStorage.getItem(key);
  if (saved) return saved;
  // Defaults per mode
  return isDarkMode() ? 'dark-default' : 'default';
}

export function reapplyThemeForMode() {
  const themeId = getSavedThemeId();
  const isDefault = themeId === 'default' || themeId === 'dark-default';
  if (isDefault) {
    clearThemeColors();
  } else {
    applyTheme(themeId);
  }
}
